import { useMutation } from '@tanstack/react-query';

import { ExceededImageSizeEventAdapter } from '~analytics/adapters/ExceededImageSizeEventAdapter';
import {
  ImageAddMethod,
  ImageUploadContext,
} from '~analytics/adapters/ImageEventAdapterTypes';
import { UnsupportedImageFormatEventAdapter } from '~analytics/adapters/UnsupportedImageFormatEventAdapter';
import { UploadImageEventAdapter } from '~analytics/adapters/UploadImageEventAdapter';

import {
  InvalidImageSizeError,
  OnUploadProgress,
  UnSupportedImageTypeError,
  getImageDimensions,
  uploadImage,
} from '../../../api/images';

interface UploadImageVariables {
  files: Array<File>;
  addMethod: ImageAddMethod;
}

type UploadImageContext = {
  start: number;
  end: number | null;
  width: number;
  height: number;
};

export function useUploadImage({
  onUploadProgress,
  context,
}: {
  onUploadProgress?: OnUploadProgress;
  context: ImageUploadContext;
}) {
  return useMutation({
    onMutate: async ({ files }) => {
      const image = files[0];

      const { width, height } = await getImageDimensions(image);
      return {
        start: performance.now(),
        end: null,
        width,
        height,
      };
    },
    mutationFn: async ({ files }: UploadImageVariables) => {
      return uploadImage({ files, onUploadProgress: onUploadProgress });
    },

    onSettled: (_data, error, _variables, context: UploadImageContext) => {
      if (typeof error === 'undefined') {
        context.end = performance.now();
      }
    },

    onError: (error, variables, { width, height }: UploadImageContext) => {
      const { type, size } = variables.files[0];
      if (error instanceof UnSupportedImageTypeError) {
        new UnsupportedImageFormatEventAdapter({
          context,
          add_method: variables.addMethod,
          file_type: type,
          file_size: size,
          width,
          height,
        }).sendAnalytics();
      }

      if (error instanceof InvalidImageSizeError) {
        new ExceededImageSizeEventAdapter({
          context,
          add_method: variables.addMethod,
          file_type: type,
          file_size: size,
          width,
          height,
        }).sendAnalytics();
      }
    },

    onSuccess: (
      data,
      { files, addMethod },
      { start, end, width, height }: UploadImageContext
    ) => {
      const { type, size } = files[0];
      new UploadImageEventAdapter({
        context,
        add_method: addMethod,
        file_type: type,
        file_size: size,
        width,
        height,
        upload_duration: end - start,
      }).sendAnalytics();
    },
  });
}
